import React from "react";
import { Accordion, Badge, Button, ListGroup, OverlayTrigger, Tooltip } from "react-bootstrap";
import { Clipboard } from "react-bootstrap-icons";
import { ValidationResult } from "../api/generate/store";
import Label from "./Label";
import useUXStore from "../api/ux/store";

export type ValidationGroup = {
    clientId: string;
    clientName: string;
    issues: ValidationResult[];
};

type ValidationPanelProps = {
    isLoading: boolean;
    groups: ValidationGroup[];
};

const fallbackCopy = (value: string) => {
    const textarea = document.createElement("textarea");
    textarea.value = value;
    textarea.style.position = "fixed";
    textarea.style.opacity = "0";
    document.body.appendChild(textarea);
    textarea.focus();
    textarea.select();
    try {
        document.execCommand("copy");
    } finally {
        document.body.removeChild(textarea);
    }
};

const ValidationPanel: React.FC<ValidationPanelProps> = ({ groups, isLoading }) => {
    const addToast = useUXStore((state) => state.addToast);

    if (isLoading) {
        return <div className="table">
            <p className="text-muted">
                <Label textAlign="center" padding="50px 0px">Loading validation results...</Label></p>
        </div>;
    }
    if (!groups.length) {
        return <div className="table">
         <p className="text-muted">
            <Label textAlign="center" padding="50px 0px">No validation issues detected.</Label></p>
        </div>;
    }

    return (
        <div className="table">
            <Accordion alwaysOpen>
                {groups.map((group, index) => {
                    const eventKey = `${group.clientId}-${index}`;

                    const copyClientId = async () => {
                        const value = group.clientId;
                        let copied = false;

                        if (navigator?.clipboard?.writeText) {
                            try {
                                await navigator.clipboard.writeText(value);
                                copied = true;
                            } catch {
                                copied = false;
                            }
                        }

                        if (!copied) {
                            try {
                                fallbackCopy(value);
                                copied = true;
                            } catch {
                                copied = false;
                            }
                        }

                        if (copied) {
                            addToast("Copied", `Client ID ${value} copied to clipboard`, null, "success");
                        } else {
                            addToast("Copy Failed", "Unable to copy client ID", null, "danger");
                        }
                    };

                    return (
                        <Accordion.Item eventKey={eventKey} key={eventKey} className="validation-group">
                            <Accordion.Header>
                                <div className="d-flex justify-content-between w-100 align-items-center">
                                    <span className="d-flex align-items-center gap-2">
                                        <OverlayTrigger
                                            placement="top"
                                            overlay={<Tooltip id={`copy-${eventKey}`}>Copy client ID</Tooltip>}
                                        >
                                            <Button
                                                type="button"
                                                size="sm"
                                                variant="outline-secondary"
                                                className="copy-client-btn p-1 d-flex align-items-center justify-content-center"
                                                onClick={(event) => {
                                                    event.stopPropagation();
                                                    void copyClientId();
                                                }}
                                            >
                                                <Clipboard size={14} />
                                            </Button>
                                        </OverlayTrigger>
                                        <span>
                                            <strong>{group.clientId}</strong>
                                            {group.clientName ? ` — ${group.clientName}` : ""}
                                        </span>
                                    </span>
                                    <Badge bg="danger">{group.issues.length}</Badge>
                                </div>
                            </Accordion.Header>
                            <Accordion.Body>
                                <ListGroup variant="flush">
                                    {group.issues.map((issue, idx) => (
                                        <ListGroup.Item key={`${eventKey}-${idx}`}>
                                            <div><strong>{issue.field || "(Unknown Field)"}</strong></div>
                                            <div className="text-muted small">{issue.reason}</div>
                                        </ListGroup.Item>
                                    ))}
                                </ListGroup>
                            </Accordion.Body>
                        </Accordion.Item>
                    );
                })}
            </Accordion>
        </div>
    );
};

export default ValidationPanel;
